import { Component, EventEmitter, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatSort } from '@angular/material/sort';
import { Subscription } from 'rxjs';
import { SecurityService } from '../../../../shared-components/security/security.service';
import { SharedService } from '../../../../shared-components/shared.service';
import { UserPreferencesService } from '../../../../shared-components/user-preferences/user-preferences.service';
import { User } from '../../../../shared-components/user/user.model';
import { Year } from '../../../../shared-components/year/year.model';
import { YearService } from '../../../../shared-components/year/year.service';
import { ClassroomSummaryService } from '../../../classroom-summary/classroom-summary.service';
import { Classroom360Service } from '../../../classroom360.service';
import { GradebookV9Service } from '../../../gradebook/v9/gradebook-v9.service';
import { GradebookAccessDeniedComponent } from '../../../gradebook/v9/modals/gradebook-access-denied/gradebook-access-denied.component';
import { NoClassSelectedWarningDialogComponent } from '../no-class-selected-warning-dialog/no-class-selected-warning-dialog.component';
import { TylerMatTableDataSource } from './../../../../shared-components/angular-material/extended.class';
import { System } from './../../../../shared-components/System';
import { UserService } from './../../../../shared-components/user/user.service';
import { LoginService } from './../../../../shared-modules/login/login.service';
import { FilterClass } from './../../../classroom-summary/classFilter.pipe';
import { ClassroomSummaryFilter } from './../../../classroom-summary/models/classroom-summary-filter';
import { ClassroomSummary } from './../../../classroom-summary/models/classroom-summary.model';
import { ClassroomTerm } from './../../../classroom-summary/models/classroom-term.model';

/**
 * This course forces a class selection. It takes the following injected properties:
 *
 * headerText: this is the text that shows in the header
 * multiselect: this determines if multiple courses should be selected
 *
 * It returns the selected class(es) on a close
 */
@Component({
    selector: 'select-course-dialog',
    templateUrl: './select-course-dialog.component.html',
    styleUrls: [
        './select-course-dialog.component.css'
    ]
})
export class SelectCourseDialogComponent implements OnInit, OnDestroy {

    // Injected Properties
    headerText = 'Select Class';
    multiselect = false;

    // Properties
    private _sort: MatSort;
    terms: ClassroomTerm[];
    selectedTerm: ClassroomTerm;
    classes: ClassroomSummary[];
    tableData = new TylerMatTableDataSource();
    V10: boolean;
    defaultYear: Year;
    columns = ['meets', 'term', 'course section', 'course name', 'room', 'enrolled', 'gradebook updated'];
    userIsTeacher: boolean;
    showFilter = new EventEmitter<any>();
    filterData: ClassroomSummaryFilter;
    subscriptions: Subscription[] = [];
    viewOptions: any;
    selectedView: any;
    selectedCourses: ClassroomSummary[] = [];
    spinner = false;
    disableView: boolean;
    showAllCourses = false;
    currentUser: User;
    teacherAndAdmin = false;

    // Class Functions
    constructor(
        private classroomSummaryService: ClassroomSummaryService,
        private loginService: LoginService,
        private userService: UserService,
        private classroom360Service: Classroom360Service,
        private userPreferencesService: UserPreferencesService,
        private gradebookV9Service: GradebookV9Service,
        private dialogBox: MatDialogRef<SelectCourseDialogComponent>,
        private filterClass: FilterClass,
        private sharedService: SharedService,
        private yearService: YearService,
        private securityService: SecurityService,
        @Inject(MAT_DIALOG_DATA) private data: any,
        private dialog: MatDialog
    ) {
        this.defaultYear = this.yearService.defaultYear;
    }

    get sort() {
        return this._sort;
    }

    @ViewChild(MatSort, { static: true }) set sort(value: MatSort) {
        this._sort = value;
        this.tableData.sort = value;
    }

    get allSelected(): boolean {
        for (let i = 0; i < this.tableData.filteredData.length; i++) {
            if (!this.selectedCourses.includes(this.tableData.filteredData[i])) {
                return false;
            }
        }
        return true;
    }

    set allSelected(value: boolean) {
        if (value) {
            this.selectedCourses = [];
            this.tableData.filteredData.forEach(course => {
                this.selectedCourses.push(course);
            });
        } else if (this.selectedCourses.length >= this.tableData.filteredData.length) {
            this.tableData.filteredData.forEach(course => {
                const index = this.selectedCourses.indexOf(course);
                if (index >= 0) {
                    this.selectedCourses.splice(index, 1);
                }
            });
        }
    }

    // Event Functions
    ngOnInit() {
        // PRE: injected dialog data with terms
        // POST: initializes the data

        // Setting the injected Properties
        if (this.data) {
            if (this.data.headerText) {
                this.headerText = this.data.headerText;
            }
            if (this.data.multiselect) {
                this.multiselect = this.data.multiselect;
            }
        }

        // Setting the service properties
        this.terms = this.classroomSummaryService.terms;
        this.userIsTeacher = this.userService.currentUser.UserIsTeacher;
        this.currentUser = this.userService.currentUser;
        this.showAllCourses = this.userIsTeacher;
        this.V10 = this.loginService.LoginSettings.V10;
        if (this.classroomSummaryService.selectedTerm) {
            this.selectedTerm = this.classroomSummaryService.selectedTerm;
        }
        else {
            this.selectedTerm = this.terms.find(term => term.Id == 0);
        }

        if (this.classroomSummaryService.showAllClasses) {
            this.showAllCourses = this.classroomSummaryService.showAllClasses;
        }
        else {
            this.showAllCourses = false;
        }

        if (this.multiselect) {
            this.columns = System.insertAt<string>(this.columns, 'select', 0);
        }
        if (!this.userIsTeacher) {
            this.columns = System.insertAt<string>(this.columns, 'instructor', 4);
        }
        if (this.V10) {
            this.columns = System.insertAt<string>(this.columns, 'profile', 6);
        }
        if (this.classroomSummaryService.filterData) {
            this.filterData = this.classroomSummaryService.filterData;
        } else {
            this.filterData = new ClassroomSummaryFilter();
        }
        this.viewOptions = this.classroomSummaryService.viewOptions;
        this.selectedTerm = this.classroomSummaryService.selectedTerm;
        this.selectedView = this.classroomSummaryService.selectedView;

        // Syncing the data
        this.subscriptions.push(
            this.classroomSummaryService.observableSelectedTermStream$.subscribe(selectedTerm => {
                this.selectedTerm = selectedTerm;
            }),
            this.classroomSummaryService.observableSelectedViewStream$.subscribe(selectedView => {
                this.selectedView = selectedView;
            }),
            this.classroomSummaryService.observableMyClassesStream$.subscribe(classes => {
                if (this.classes == undefined) {
                    if (this.classroom360Service.gradebookPortalSelected) {
                        this.disableView = true;
                        this.selectedView = this.viewOptions.filter(v => v.value.toString() == '3')[0];
                        this.classroomSummaryService.getGradebookAttendanceClasses(true, this.selectedTerm.Id).subscribe((courses) => {
                            this.classes = courses.filter(course => course.Enrolled > 0);
                            this.setupCourses();
                        });
                    }
                    else if (this.classroom360Service.attendancePortalSelected) {
                        this.disableView = true;
                        this.selectedView = this.viewOptions.filter(v => v.value.toString() == '2')[0];
                        this.classroomSummaryService.getGradebookAttendanceClasses(false, this.selectedTerm.Id).subscribe((courses) => {
                            this.classes = courses.filter(course => course.Enrolled > 0);
                            this.setupCourses();
                        });
                    }
                    else if (this.classroom360Service.courseInfoPortalSelected) {
                        this.disableView = true;
                        if (this.selectedView.value.toString() != '1') {
                            this.selectedView = this.viewOptions.filter(v => v.value.toString() == '1')[0];
                            this.classroomSummaryService.getClassesWithoutBroadcast(this.defaultYear.Id, this.defaultYear.SchoolID, this.selectedTerm.Id, this.selectedView.value).subscribe((courses) => {
                                this.classes = courses;
                                this.setupCourses();
                            });
                        }
                        else {
                            this.classes = this.classroomSummaryService.myClasses;
                        }
                    }
                    else {
                        this.classes = classes;
                    }
                    if (this.classes) {
                        this.setupCourses();
                    }
                }
            }),
            this.classroomSummaryService.observableShowAllClassesStream$.subscribe(showAllClasses => {
                this.showAllCourses = this.V10 == false ? showAllClasses : false;
            })
        );

        this.setTeacherAdminMode();
        // If Class Information Portal is selected and user has Admin access for Class information then show all courses.
        if (!this.V10 && this.classroom360Service.courseInfoPortalSelected && this.userService.currentUser.UserIsTeacher) {
            this.userHasAdminAccessGetAllClasses();
        }
    }

    userHasAdminAccessGetAllClasses() {
        let userHasAdminPermission = false;
        if (this.securityService.IsAnyPermissionCodeAssigned(...['GO_MCIA', 'GO_VCIA'])) {
            userHasAdminPermission = true;
        }
        if (userHasAdminPermission) {
            this.showAllCourses = true;
            this.getAllCourses();
        }
    }

    setupCourses() {
        const ssecIds = [];
        for (const course of this.classes) {
            ssecIds.push(course.SSEC_ID);
        }
        if (this.V10) {
            this.classroomSummaryService.getCourseProfileExistence(ssecIds).subscribe((result) => {
                for (const course of this.classes) {
                    const courseHasProfile = result.find(profile => profile.SSEC_ID == course.SSEC_ID);
                    if (courseHasProfile && courseHasProfile.HasProfile) {
                        course.CourseHasProfile = true;
                    }
                    else {
                        course.CourseHasProfile = false;
                    }
                }
            });
        }

        // Arranging the table
        if (this.V10 == false && this.classroom360Service.gradebookPortalSelected) {
            this.tableData.data = this.classes.filter(c => c.UsedForGrading == true);
        }
        else if (this.V10 == false && this.classroom360Service.attendancePortalSelected) {
            this.tableData.data = this.classes.filter(c => c.Enrolled > 0 && c.AttendanceRequired == true);
        }
        else {
            this.tableData.data = this.classes.filter(c => c.Enrolled > 0);
        }
        this.tableData.filters = (this.classroomSummaryService.classSourceFilters) ? this.classroomSummaryService.classSourceFilters : this.tableData.filters;
    }

    ngOnDestroy() {
        // POST: unsubscribes to all subscriptions
        this.subscriptions.forEach(subscription => {
            subscription.unsubscribe();
        });
    }

    save() {
        // PRE: the save button is clicked (which means that this is a multiselect)
        // POST: checks the number of selected courses.
        //       If there are some, closes with the list of selected courses
        //       If there are none, shows a warning dialog
        if (this.selectedCourses.length > 0) {
            this.dialogBox.close(this.selectedCourses);
        } else {
            this.dialog.open(NoClassSelectedWarningDialogComponent, {
                disableClose: true
            });
        }
    }

    cancel() {
        // PRE: the cancel button or the background is clicked
        // POST: closes the dialog box
        this.classroom360Service.gradebookPortalSelected = false;
        this.classroom360Service.attendancePortalSelected = false;
        this.classroom360Service.courseInfoPortalSelected = false;
        this.dialogBox.close();
    }

    filter(filter?: ClassroomSummaryFilter) {
        // PRE: the filter button is clicked
        // POST: opens the filter
        if (filter) {
            this.filterData = filter;
            this.classroomSummaryService.filterData = filter;
        }
        if (this.V10 == false && this.classroom360Service.gradebookPortalSelected) {
            this.tableData.data = this.filterClass.transform(this.classes.filter(o => o.UsedForGrading == true), this.filterData);
        }
        else {
            this.tableData.data = this.filterClass.transform(this.classes, this.filterData);
        }
        this.classroomSummaryService.filterChange.emit();
    }

    clearFilter() {
        // PRE: the clear filter button is clicked (in the filter notification)
        // POST: clears out the filter and applies it
        // NOTE: We don't need to perform a manual save because the classroom summary filter resets it on each push
        Object.assign(this.filterData, new ClassroomSummaryFilter());
        this.filter();
    }

    setSelectedTerm(selectedTerm: ClassroomTerm) {
        // PRE: a selected term change is made
        // POST: sets the selected term
        // this.classroomSummaryService.selectedTerm = selectedTerm;
        this.getClasses();
    }

    setSelectedView(selectedView: any) {
        // PRE: a selected view change is made
        // POST: sets the selected view
        // this.classroomSummaryService.selectedView = selectedView;
        this.getClasses();
    }

    selectCourse(course: ClassroomSummary) {
        // PRE: a course is selected from the view
        // POST: selects a course. If this is a single selection, closes the dialog with that course.
        if (this.multiselect) {
            const indexOfCourse = this.selectedCourses.indexOf(course);
            if (indexOfCourse >= 0) {
                this.selectedCourses.splice(indexOfCourse, 1);
            } else {
                this.selectedCourses.push(course);
            }
        } else {
            if (this.classroom360Service.gradebookPortalSelected) {
                this.classroom360Service.gradebookPortalSelected = false;
                this.classroom360Service.attendancePortalSelected = false;
                this.classroom360Service.courseInfoPortalSelected = false;
                let portalName = 'Gradebook';
                if (this.sharedService.currentPortal != null) {
                    portalName = this.sharedService.currentPortal.PortalName;
                    if (portalName == 'Gradebook' && (course.GradeScaleExists == false || course.GradebookAccess == false)) {
                        const gradebookAccessDeniedDialogRef = this.dialog.open(GradebookAccessDeniedComponent, {
                            panelClass: 'tyl-mat-dialog',
                            width: '500px',
                            disableClose: true
                        });
                        gradebookAccessDeniedDialogRef.componentInstance.course = course;
                        this.classroom360Service.gradebookPortalSelected = true;
                    }
                    else {
                        this.gradebookV9Service.clearAllData();
                        this.gradebookV9Service.course = course;
                        if (!this.V10) {
                            if (portalName == 'Report Card Grades' || this.userPreferencesService.gradebookV9SelectionPreferences.SelectedMainTab == 'ReportCardGrades') {
                                this.gradebookV9Service.selectedTab = 'Report Card Grades';
                            }
                            else if (this.userPreferencesService.gradebookV9SelectionPreferences.SelectedMainTab == 'Indicators') {
                                this.gradebookV9Service.selectedTab = 'Scores';
                            }
                            else {
                                this.gradebookV9Service.selectedTab = this.userPreferencesService.gradebookV9SelectionPreferences.SelectedMainTab;
                            }
                            this.gradebookV9Service.nonScoreStandard = this.userPreferencesService.gradebookV9SelectionPreferences.ShowNonScoredStandards;
                        }
                        else {
                            this.gradebookV9Service.selectedTab = portalName == 'Gradebook' ? 'Scores' : 'Report Card Grades';
                        }
                        this.dialogBox.close(course);
                    }
                }
            }
            else {
                this.dialogBox.close(course);
            }
        }
    }

    // Methods
    filtered() {
        // POST: returns true if the data is being panelFiltered
        const filter = this.filterData;
        if (!filter) {
            return false;
        }
        if (!filter.AttStatusNone || !filter.AttStatusPartial || !filter.AttStatusAll || !filter.AttStatusPerfect || !filter.GradingNone ||
            !filter.GradingPartial || !filter.GradingAll || !filter.RCGNone || !filter.RCGAll || !filter.RCGEx) {
            return true;
        }
        else if ((filter.Meets != undefined && filter.Meets.length > 0) || (filter.Term != undefined && filter.Term.length > 0) ||
            (filter.CourseSection != undefined && filter.CourseSection.length > 0) || (filter.CourseName != undefined && filter.CourseName.length > 0)) {
            return true;
        }
        else {
            return false;
        }
    }

    getClasses(): void {
        // POST: gets the classes from the server
        this.classroomSummaryService.getClassesWithoutBroadcast(this.defaultYear.Id, this.defaultYear.SchoolID, this.selectedTerm.Id, Number(this.selectedView.value)).subscribe((courses) => {
            this.classes = courses;
            this.setupCourses();
        });
    }

    showAllCoursesClick() {
        this.getAllCourses();
    }

    getAllCourses() {
        this.teacherAndAdmin = !this.showAllCourses;
        this.classroomSummaryService.showAllClasses = this.showAllCourses;
        this.currentUser.UserIsTeacher = !this.showAllCourses;
        this.currentUser.TeacherAdminShowAllCourses = this.showAllCourses;
        this.getMyClasses();
    }

    setTeacherAdminMode() {
        this.showAllCourses = this.currentUser.TeacherAdminShowAllCourses;
        if (this.currentUser.UserIsTeacher) {
            if (!this.loginService.LoginSettings.V10) {
                // Add permissions for V9 here if necessary
                const classesPermission = this.securityService.userPermissions.filter(permission => permission.PermissionCode == 'CD_VSCSAR')[0];
                this.teacherAndAdmin = classesPermission != undefined && classesPermission != null ? true : false;
            }
            else {
                this.teacherAndAdmin = false;
            }
        }
        else {
            this.teacherAndAdmin = false;
        }
    }

    teacherShowAllCourses() {
        if (!this.V10) {
            return this.currentUser.TeacherAdminShowAllCourses;
        }
        else {
            return false;
        }

    }

    getMyClasses(): void {
        let getTeacherCourses = this.currentUser.UserIsTeacher;
        if (this.teacherAndAdmin) {
            getTeacherCourses = !this.showAllCourses;
        }
        const selectedTermId = this.selectedTerm.Id;
        this.subscriptions.push(this.classroomSummaryService.getMyClasses(selectedTermId, Number(this.classroomSummaryService.selectedView.value), getTeacherCourses, false, null).subscribe(classes => {
            this.classes = classes;
            this.tableData.data = this.classes;
        }));
    }
}
